import React from 'react'
import { Link as RouterLink, useHistory } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import CalendarAddOn from 'components/material-symbol/icons/CalendarAddOn'
import ChatBubble from 'components/material-symbol/icons/ChatBubble'
import ArrowForwardIcon from 'components/material-symbol/icons/ArrowForward'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import ConfirmationNumberIcon from 'components/material-symbol/icons/ConfirmationNumber'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { getInitials } from 'lib/text-display'
import { gtmEvent } from 'lib/gtm'
import {
  buildCoachProfileUrl,
  buildGoogleMapsUrl,
  buildLessonPackBookingUrl,
  formatLessonPackRemaining,
  formatLessonPackSummary,
  formatLocationLine,
  getActiveLessonPacks,
} from './utils'
import { useStyles } from './styles'

const CoachCard = ({ coach }) => {
  const classes = useStyles()
  const history = useHistory()
  const initials = getInitials(coach.name)
  const locationLine = formatLocationLine(coach)
  const facilityMapsUrl = buildGoogleMapsUrl(coach)
  const coachProfileUrl = buildCoachProfileUrl(coach)
  const activeLessonPacks = getActiveLessonPacks(coach.lessonCreditBalances)

  const handleBookClick = event => {
    event.stopPropagation()
    history.push(`/coach/${coach.coachSlug || coach.externalId}/schedule`)
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
  }

  const handleProfileClick = event => {
    if (!coachProfileUrl) {
      return
    }

    if (event.target.closest('a, button, [role="button"]')) {
      return
    }

    window.open(coachProfileUrl, '_blank', 'noopener,noreferrer')
  }

  const handleProfileKeyDown = event => {
    if (!coachProfileUrl) {
      return
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    window.open(coachProfileUrl, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box className={classes.card}>
      <Box
        className={`${classes.cardBody} ${
          coachProfileUrl ? classes.cardBodyClickable : ''
        }`}
        onClick={handleProfileClick}
        onKeyDown={handleProfileKeyDown}
        role={coachProfileUrl ? 'link' : undefined}
        tabIndex={coachProfileUrl ? 0 : undefined}
      >
        <Box className={classes.identity}>
          <Avatar
            className={classes.avatar}
            alt={coach.name}
            src={coach.coachAvatar}
          >
            {initials}
          </Avatar>
          <Box className={classes.details}>
            <Typography component="h3" className={classes.name}>
              {coach.name}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.locationRow}>
          {coach.facilityName || locationLine ? (
            <Box className={classes.locationSection}>
              <RoomOutlinedIcon className={classes.locationSectionIcon} />
              <Box className={classes.locationSectionCopy}>
                {coach.facilityName ? (
                  facilityMapsUrl ? (
                    <Typography
                      component="a"
                      href={facilityMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.facilityNameLink}
                    >
                      {coach.facilityName}
                    </Typography>
                  ) : (
                    <Typography component="p" className={classes.facilityName}>
                      {coach.facilityName}
                    </Typography>
                  )
                ) : null}
                {locationLine ? (
                  <Typography component="p" className={classes.locationLine}>
                    {locationLine}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}

          <Box className={classes.actions}>
            <Tooltip title="Book a session">
              <IconButton
                aria-label="Book a session"
                className={classes.primaryActionButton}
                onClick={handleBookClick}
              >
                <CalendarAddOn fontSize="small" />
              </IconButton>
            </Tooltip>
            <StreamSendMessageButton
              userIds={[coach.externalId]}
              component={props => (
                <Tooltip title="Message coach">
                  <IconButton
                    {...props}
                    aria-label="Message coach"
                    className={classes.borderedActionButton}
                    onClick={event => {
                      event.stopPropagation()
                      props.onClick?.(event)
                    }}
                  >
                    <ChatBubble fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            />
          </Box>
        </Box>
      </Box>

      {activeLessonPacks.length ? (
        <Box className={classes.packFooters}>
          {activeLessonPacks.map(lessonPack => {
            const lessonPackUrl = buildLessonPackBookingUrl(lessonPack)

            if (!lessonPackUrl) {
              return null
            }

            const packLabel =
              activeLessonPacks.length > 1
                ? formatLessonPackSummary(
                  lessonPack.remainingCredits,
                  lessonPack.lessonType?.title,
                )
                : formatLessonPackRemaining(lessonPack.remainingCredits)

            return (
              <Box key={lessonPack.id} className={classes.packFooter}>
                <Box className={classes.packCopy}>
                  <ConfirmationNumberIcon className={classes.packIcon} />
                  <Typography component="p" className={classes.packText}>
                    {packLabel}
                  </Typography>
                </Box>
                <Button
                  className={classes.packAction}
                  component={RouterLink}
                  to={lessonPackUrl}
                >
                  <Box component="span" className={classes.packActionLabel}>
                    Use Lesson
                    <ArrowForwardIcon
                      className={classes.packActionIcon}
                      fontSize="inherit"
                      opticalSize={14}
                    />
                  </Box>
                </Button>
              </Box>
            )
          })}
        </Box>
      ) : null}
    </Box>
  )
}

export default CoachCard
